/* E3Live C35: make shoppers actively choose a Flavor on products that have a "Flavor" option.
   - The variant picker renders a hidden marker (<span data-e3-flavor-required>) for such products.
   - Until a flavor button is clicked, neither flavor LOOKS selected (Shopify still has a default variant
     internally), the gallery shows both flavors' photos, and every way into the cart is blocked with a
     message: main Add to cart, the sticky cart bar, Buy it now, and the quick-view pop-up.
   - Links that already name a variant (?variant=…) count as a choice (redirects from the old flavor pages).
   - Inert on every product without a Flavor option. */
(() => {
  if (window.E3FlavorRequired) return;
  window.E3FlavorRequired = true;

  const MSG = 'Please choose a flavor: Unflavored or Apple/Lemon.';
  const chosen = new Set();          // product ids the shopper has picked a flavor for (this page view)
  const scopes = new WeakSet();      // scope elements already initialised
  const urlHasVariant = new URLSearchParams(location.search).has('variant');

  const css = `
    .e3-flavor-pending fieldset:has(input[name^="Flavor-"]) input[type=radio]:checked + label {
      color: rgb(var(--color-foreground)); box-shadow: inset 0 0 0 0.1rem rgb(var(--color-border)); }
    .e3-flavor-pending fieldset:has(input[name^="Flavor-"]) input.button-input__input:checked + label::after {
      background-color: transparent; }
    .e3-flavor-pending fieldset:has(input[name^="Flavor-"]) legend::after {
      content: ' — please choose'; font-weight: 400; opacity: .75; }
    .e3-flavor-pending.e3-flavor-alert fieldset:has(input[name^="Flavor-"]) input[type=radio] + label {
      box-shadow: inset 0 0 0 0.2rem #c62828; }
    .e3-flavor-pending [data-gang-option] { display: block; }
    .e3-flavor-pending .shopify-payment-button { position: relative; }
    .e3-flavor-pending .shopify-payment-button::after {
      content: ''; position: absolute; inset: 0; z-index: 5; cursor: pointer; }
    .e3-flavor-msg { color: #c62828; font-weight: 600; margin: .4rem 0 1rem; }
    .e3-flavor-msg[hidden] { display: none; }`;
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);

  const isModal = (el) => !!el.closest('quick-view-modal, .quick-view__content, [role="dialog"]');

  // The smallest ancestor holding both the variant picker and a product form. On a product page it is
  // widened to the whole Shopify section (so the sticky cart bar is included); in a quick-view pop-up it
  // is NOT widened, because the pop-up lives inside the collection section with every other card.
  function scopeOf(marker) {
    let el = marker.parentElement;
    while (el && el !== document.body) {
      if (el.querySelector('variant-selects') && el.querySelector('product-form, form[action*="/cart/add"]')) {
        return isModal(marker) ? el : (el.closest('.shopify-section') || el);
      }
      el = el.parentElement;
    }
    return null;
  }

  function init(marker) {
    const scope = scopeOf(marker);
    if (!scope || scopes.has(scope)) return;
    scopes.add(scope);
    const pid = marker.dataset.productId;
    scope.dataset.e3FlavorProduct = pid;
    const inModal = isModal(marker);
    if (chosen.has(pid) || (urlHasVariant && !inModal)) return;
    scope.classList.add('e3-flavor-pending');
  }

  function pendingScope(el) {
    const scope = el.closest('.e3-flavor-pending');
    return scope && !chosen.has(scope.dataset.e3FlavorProduct) ? scope : null;
  }

  function showMessage(scope) {
    scope.classList.add('e3-flavor-alert');
    const picker = scope.querySelector('variant-selects');
    let msg = scope.querySelector('.e3-flavor-msg');
    if (!msg && picker) {
      msg = document.createElement('p');
      msg.className = 'e3-flavor-msg';
      msg.setAttribute('role', 'alert');
      picker.insertAdjacentElement('afterend', msg);
    }
    if (msg) { msg.hidden = false; msg.textContent = MSG; }
    const fs = [...scope.querySelectorAll('variant-selects fieldset')].find((f) => f.querySelector('input[name^="Flavor-"]'));
    if (fs) {
      fs.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const first = fs.querySelector('input[name^="Flavor-"]');
      if (first) first.focus({ preventScroll: true });
    }
  }

  function confirm(scope) {
    chosen.add(scope.dataset.e3FlavorProduct);
    scope.classList.remove('e3-flavor-pending', 'e3-flavor-alert');
    const msg = scope.querySelector('.e3-flavor-msg');
    if (msg) msg.hidden = true;
  }

  // A flavor click (even on the already-checked button) counts as the choice.
  document.addEventListener('click', (e) => {
    const input = e.target.closest('input[name^="Flavor-"], label');
    if (!input) return;
    const real = input.matches('label') ? document.getElementById(input.htmlFor) : input;
    if (!real || !real.name || !real.name.startsWith('Flavor-')) return;
    const scope = real.closest('[data-e3-flavor-product]');
    if (scope && scope.classList.contains('e3-flavor-pending')) confirm(scope);
  }, true);

  // Block every add-to-cart path while no flavor has been chosen.
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    const scope = pendingScope(form);
    if (!scope) return;
    e.preventDefault(); e.stopImmediatePropagation();
    showMessage(scope);
  }, true);

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('button[name="add"], [type="submit"][form], .shopify-payment-button, .product-form__submit, .sticky-cart__form button');
    if (!btn) return;
    const scope = pendingScope(btn);
    if (!scope) return;
    e.preventDefault(); e.stopImmediatePropagation();
    showMessage(scope);
  }, true);

  const scan = (root) => root.querySelectorAll && root.querySelectorAll('[data-e3-flavor-required]').forEach(init);
  const start = () => {
    scan(document);
    new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach((n) => n.nodeType === 1 && scan(n.parentElement || n))))
      .observe(document.body, { childList: true, subtree: true });
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
